import type { SearchResult } from '@amzn/das-finsuite-shared-ui';
import { GlobalSearchBar as SharedGlobalSearchBar } from '@amzn/das-finsuite-shared-ui';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEmbeddedSearch } from 'src/hooks/useEmbeddedSearch';
import { RecentSearchesService } from 'src/services/recents/RecentSearchesService';
import { RecentsService } from 'src/services/recents/RecentsService';
import { RecentEntityType } from 'src/services/recents/RecentsConstants';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { buildSections } from './GlobalSearchUtils';
import { createFilterTagsRenderer, createResultActionsRenderer, ProxySearchBar } from './GlobalSearchRenderers';
import { useFavoriteToggle } from './hooks/useFavoriteToggle';
import { useFinSuiteSearch } from './hooks/useFinSuiteSearch';
import { useProductFilter } from './hooks/useProductFilter';
import { useSearchFocusTracking } from './hooks/useSearchFocusTracking';

interface GlobalSearchBarProps {
  isEmbedActive?: boolean;
}

export const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({ isEmbedActive = false }) => {
  const navigate = useNavigate();
  const { Alias: userAlias, CognitoSub } = useAuth();
  const embeddedSearch = useEmbeddedSearch(isEmbedActive);
  const { query, setQuery, results } = useFinSuiteSearch();
  const { selectedGlAccount, setSelectedGlAccount, filteredProducts, glAccountTags } = useProductFilter(results.products);
  const { toggleFavorite } = useFavoriteToggle();
  const { handleEnsureDataLoaded, wrapperRef } = useSearchFocusTracking(setSelectedGlAccount, query, setQuery);

  const sections = useMemo(() => buildSections(results, filteredProducts), [results, filteredProducts]);

  const handleResultClick = useCallback(
    (result: SearchResult) => {
      if (query.trim()) {
        RecentSearchesService.record(query);
      }
      if (result.type === 'announcements') {
        navigate('/announcements', { state: { filterByTitle: result.label } });
        return;
      }
      if (result.id.startsWith('calendar-')) {
        navigate('/', { state: { highlightCalendarEvent: { scenario: result.meta, event: result.label } } });
        return;
      }
      if (result.type === 'products') {
        RecentsService.recordVisit(CognitoSub, userAlias, RecentEntityType.APP, result.id, { isExternal: !result.embeddingEnabled });
        if (result.embeddingEnabled && result.path) {
          navigate(result.path);
        } else if (result.path) {
          window.open(result.path, '_blank', 'noopener,noreferrer');
        }
        return;
      }
      if (result.externalUrl) {
        window.open(result.externalUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      if (result.path?.startsWith('/')) {
        navigate(result.path);
      } else if (result.path) {
        window.open(result.path, '_blank', 'noopener,noreferrer');
      }
    },
    [navigate, query, CognitoSub, userAlias]
  );

  const renderResultActions = useMemo(() => createResultActionsRenderer(toggleFavorite), [toggleFavorite]);
  const renderFilterTags = useMemo(
    () => createFilterTagsRenderer(glAccountTags, selectedGlAccount, setSelectedGlAccount),
    [glAccountTags, selectedGlAccount, setSelectedGlAccount]
  );

  if (embeddedSearch.capabilities) {
    return <ProxySearchBar embeddedSearch={embeddedSearch} />;
  }

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <SharedGlobalSearchBar
        variant="finsuite"
        placeholder="Search FinSuite  ·  apps, product launches, links..."
        sections={sections}
        onQueryChange={setQuery}
        onResultClick={handleResultClick}
        onEnsureDataLoaded={handleEnsureDataLoaded}
        highlightQuery
        renderResultActions={renderResultActions}
        renderFilterTags={renderFilterTags}
      />
    </div>
  );
};
